import apiClient from "config/apiClient";
import {
  ICarModelItem,
  IMarkerItem,
  ICarTireSizeItem,
  ICarTireSizeUp,
  ITireItemDetail,
  Maker,
  EstCreateRequest, Category,
  EstWheelCreateRequest
} from "../types";

export const CAR_URL = {
  MARKER: "/mst/car/maker/select",
  MODEL: "/mst/car/model/select",
  TIRE_SIZE: "/mst/car/tiresize/select",
  TIRE_SIZE_UP: "/mst/car/tiresize/up/select",
  TIRE_DETAIL: "/mst/item/detail/tire/select",
  CREATE_QUOTE: "tbl/quote/create",
  SPECIAL_PRICE: "mst/item/detail/wheel/special/price/select",
}

/**
 * Get the rounded value based on the rounding option.
 * @param {number} value - The value to round.
 * @param {number} rounding - The rounding option to use.
 * @returns {number} - The rounded value.
 */
export function getRoundedValue(value: number, rounding: number): number {
  switch (rounding) {
    case 0:
      return value;
    case 1:
      return Math.floor(value / 10) * 10;
    case 2:
      return Math.floor(value / 100) * 100;
    case 3:
      return Math.floor(value / 1000) * 1000;
    case 4:
      return Math.floor(value / 10000) * 10000;
    default:
      return value;
  }
  return value;
}

interface MarkerListResponse {
  list: IMarkerItem[];
  total: number;
}

interface CarModelListResponse {
  list: ICarModelItem[];
  total: number;
}

interface TireSizeListResponse {
  list: ICarTireSizeItem[];
  total: number;
}

interface UpTireSizeListResponse {
  list: ICarTireSizeUp;
  total: number;
}

export interface TireSizeRequest {
  car_brand: string;
  car_model: number;
}

export interface TireResponse {
  list: {
    category_list: Category[];
    maker_list: Maker[];
    mst_item_detail_tire: ITireItemDetail[];
    mst_proc_fee: any;
    store?: {
      price_setting_tire?: number;
      price_setting_wheel?: number;
      price_setting_wheel_low_price?: number;
    };
  };
  total: number;
}

export interface SpecialPriceResponse {
  price_tire: number;
  price_wheel: number;
}

export interface SpecialPriceRequest {
  tire_item_cd: string;
  wheel_item_id: string;
  store_cd: string;
}

/**
 * Fetches a list of markers from the API.
 * @param {object} [body] - The request payload to send to the API.
 * @returns {Promis
 * 
 e<MarkerListResponse>} - A promise that resolves to the response data containing the marker list and total count.
 */
const getMarkerApi = async (body: object = {}): Promise<MarkerListResponse> => {
  const res = await apiClient.post<MarkerListResponse>(CAR_URL.MARKER, body);
  return res.data!;
};

/**
 * Fetches car model data from the API.
 * @param {object} body - The request payload to send to the API.
 * @param {string} body.modl_brand - The brand of the car model to fetch.
 * @returns {Promise<CarModelListResponse>} - A promise that resolves to the response data containing car model information.
 */
const getCarModelApi = async (body: { modl_brand: string }): Promise<CarModelListResponse> => {
  const res = await apiClient.post<CarModelListResponse>(CAR_URL.MODEL, body);
  return res.data!;
};

/**
 * Fetches tire size data from the API.
 * @param {object} body - The request payload to send to the API.
 * @param {string} body.car_brand - The brand of the car.
 * @param {number} body.car_model - The model ID of the car.
 * @returns {Promise<TireSizeListResponse>} - A promise that resolves to the response data containing tire size information.
 */
const getTireSizeApi = async (body: TireSizeRequest): Promise<TireSizeListResponse> => {
  const res = await apiClient.post<TireSizeListResponse>(CAR_URL.TIRE_SIZE, body);
  return res.data!;
};

/**
 * Fetches updated tire size data from the API.
 * @param {object} body - The request payload to send to the API.
 * @param {number} body.car_tiresize_id - The ID of the tire size to fetch updates for.
 * @returns {Promise<UpTireSizeListResponse>} - A promise that resolves to the response data containing updated tire size information.
 */
const upTireSizeApi = async (body: {
  car_tiresize_id: number;
}): Promise<UpTireSizeListResponse> => {
  const res = await apiClient.post<UpTireSizeListResponse>(CAR_URL.TIRE_SIZE_UP, { ...body, mode: "tire" });
  return res.data!;
};

/**
 * Fetches tire details by product code from the API.
 * @param {object} body - The request payload to send to the API.
 * @param {string} body.item_cd - The product code of the tire to fetch details for.
 * @returns {Promise<TireResponse>} - A promise that resolves to the response data containing tire details.
 */
const getTireDetailByProductCodeApi = async (body: { item_cd: string }): Promise<TireResponse> => {
  const res = await apiClient.post<TireResponse>(CAR_URL.TIRE_DETAIL, body);
  return res.data!;
};

/**
 * Creates a new quote in the system.
 * @param {EstCreateRequest} body - The request payload containing quote details.
 * @returns {Promise<{ quote_id: string }>} - A promise that resolves to the response data containing the created quote ID.
 */
const createQuoteApi = async (
  body: EstCreateRequest,
): Promise<{ quote_id: string } | undefined> => {
  const filteredQuoteDetailTire = body.tbl_quote_detail_tire
    .filter((item) => item.checked)
    .map((item) => ({
      ...item,
      checked: undefined,
      confirm_total_price: getRoundedValue(item.confirm_total_price, Number(body.round_type)),
    }));
  const res = await apiClient.post<{ quote_id: string }>(CAR_URL.CREATE_QUOTE, {
    ...body,
    tbl_quote_detail_tire: filteredQuoteDetailTire,
  });
  return res.data;
};

/**
 * Creates a new wheel quote in the system.
 * @param {EstWheelCreateRequest} body - The request payload containing wheel quote details.
 * @returns {Promise<{ quote_id: string }>} - A promise that resolves to the response data containing the created quote ID.
 */
const createWheelQuoteApi = async (
  body: EstWheelCreateRequest,
): Promise<{ quote_id: string } | undefined> => {
  const res = await apiClient.post<{ quote_id: string }>(CAR_URL.CREATE_QUOTE, {
    ...body,
  });
  return res.data;
};

/**
 * Fetches the special price for a tire and wheel from the API.
 * @param {SpecialPriceRequest} body - The request payload containing the tire and wheel item codes and store code.
 * @returns {Promise<SpecialPriceResponse | undefined>} - A promise that resolves to the response data containing the special price for the tire and wheel.
 */
const getSpecialPriceApi = async (body: SpecialPriceRequest): Promise<SpecialPriceResponse | undefined> => {
  const res = await apiClient.post<SpecialPriceResponse>(CAR_URL.SPECIAL_PRICE, body);
  return res.data;
};

export {
  getMarkerApi,
  getCarModelApi,
  getTireSizeApi,
  upTireSizeApi,
  getTireDetailByProductCodeApi,
  createQuoteApi,
  getSpecialPriceApi,
  createWheelQuoteApi,
};
